import { Switch, Route, Router as WouterRouter } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { InitialDataProvider } from "@/lib/initial-data-context";
import type { InitialData } from "@/lib/initial-data";
import Layout from "@/components/layout";
import Home from "@/pages/home";
import Menu from "@/pages/menu";
import VipProgram from "@/pages/vip-program";
import PetPalVisits from "@/pages/pet-pal-visits";
import PetSitting from "@/pages/pet-sitting";

import Reviews from "@/pages/reviews";
import OurTeam from "@/pages/our-team";
import JoinOurTeam from "@/pages/join-our-team";
import Blog from "@/pages/blog";
import BlogPost from "@/pages/blog-post";
import ContactUs from "@/pages/contact-us";
import Faqs from "@/pages/faqs";
import Giving from "@/pages/giving";
import ActiveRetireeProgram from "@/pages/active-retiree-program";
import About from "@/pages/about";
import ServiceAreas from "@/pages/service-areas";
import PoliciesRates from "@/pages/policies-rates";
import Gallery from "@/pages/gallery";
import CarePlanFinder from "@/pages/care-plan-finder";
import Resources from "@/pages/resources";
import GuideSummerSafety from "@/pages/guide-summer-safety";
import GuideHurricanePrep from "@/pages/guide-hurricane-prep";
import GuideNewPetWelcomeKit from "@/pages/guide-new-pet-welcome-kit";
import AdminDashboard from "@/pages/admin/dashboard";
import NotFound from "@/pages/not-found";
import NewClientOffer from "@/pages/new-client-offer";

const queryClient = new QueryClient();

function Router() {
  return (
    <Switch>
      <Route path="/admin" component={AdminDashboard} />
      <Route path="/new" component={NewClientOffer} />
      <Route>
        <Layout>
          <Switch>
            <Route path="/" component={Home} />
            <Route path="/home" component={Home} />
            <Route path="/menu" component={Menu} />
            <Route path="/services" component={Menu} />
            <Route path="/pet-pal-visits" component={PetPalVisits} />
            <Route path="/pet-sitting" component={PetSitting} />
            <Route path="/vip-program" component={VipProgram} />

            <Route path="/reviews" component={Reviews} />
            <Route path="/our-team" component={OurTeam} />
            <Route path="/join-our-team" component={JoinOurTeam} />
            <Route path="/blog" component={Blog} />
            <Route path="/blog/:slug" component={BlogPost} />
            <Route path="/contact-us" component={ContactUs} />
            <Route path="/contact" component={ContactUs} />
            <Route path="/faqs" component={Faqs} />
            <Route path="/giving" component={Giving} />
            <Route path="/active-retiree-program" component={ActiveRetireeProgram} />
            <Route path="/about" component={About} />
            <Route path="/service-areas" component={ServiceAreas} />
            <Route path="/policies-rates" component={PoliciesRates} />
            <Route path="/rates" component={PoliciesRates} />
            <Route path="/gallery" component={Gallery} />
            <Route path="/care-plan-finder" component={CarePlanFinder} />
            <Route path="/service-finder" component={CarePlanFinder} />
            <Route path="/find-my-plan" component={CarePlanFinder} />
            <Route path="/resources" component={Resources} />
            <Route path="/resources/summer-safety" component={GuideSummerSafety} />
            <Route path="/resources/hurricane-prep" component={GuideHurricanePrep} />
            <Route path="/resources/new-pet-welcome-kit" component={GuideNewPetWelcomeKit} />
            <Route path="/lend-a-leash">{() => { if (typeof window !== "undefined") window.location.href = "/lend-a-leash/"; return null; }}</Route>
            <Route path="/community">{() => { if (typeof window !== "undefined") window.location.href = "/lend-a-leash/"; return null; }}</Route>
            <Route component={NotFound} />
          </Switch>
        </Layout>
      </Route>
    </Switch>
  );
}

function App({ ssrPath, initialData }: { ssrPath?: string; initialData?: InitialData }) {
  return (
    <InitialDataProvider data={initialData}>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")} ssrPath={ssrPath}>
              <Router />
            </WouterRouter>
            <Toaster />
          </TooltipProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </InitialDataProvider>
  );
}

export default App;
